/**
 * Partner-access check + signed-in profile.
 *
 * Firebase tells us *who* a user is; it does not tell us whether they may use
 * the Partner Portal, nor their pop.in profile. After sign-in we ask the pop.in
 * GraphQL API for the caller's public profile and global roles (the same
 * `self` the admin-gui reads) and allow them in only if they hold one of
 * {@link PARTNER_ROLES}.
 */

import { getIdToken } from "firebase/auth";

import { GRAPHQL_ENDPOINT, PARTNER_ROLES, SUPER_ADMIN_ROLE } from "@/config";
import { getFirebaseAuth } from "@/lib/firebase";
import { BackendUnavailableError } from "@/lib/backend-error";

const SELF_QUERY = /* GraphQL */ `
  query PartnerPortalSelf {
    self {
      public {
        displayName
        username
        photoURL
      }
      globalRoles {
        nodes {
          permalink
        }
      }
    }
  }
`;

interface SelfResponse {
  data?: {
    self?: {
      public?: {
        displayName?: string | null;
        username?: string | null;
        photoURL?: string | null;
      } | null;
      globalRoles?: {
        nodes?: Array<{ permalink: string | null }>;
      } | null;
    } | null;
  };
  errors?: Array<{ message: string }>;
}

/** The signed-in user's portal-relevant profile. */
export interface SelfProfile {
  roles: string[];
  displayName: string;
  username: string;
  /** Profile picture URL (may be empty). */
  photoURL: string;
}

/**
 * Fetch the signed-in user's profile + global roles. Returns `null` when signed
 * out or when the backend has no `self` record for the account.
 */
export async function fetchSelf(signal?: AbortSignal): Promise<SelfProfile | null> {
  const auth = getFirebaseAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) return null;

  if (!GRAPHQL_ENDPOINT) {
    throw new Error("BUN_PUBLIC_GRAPHQL_ENDPOINT is not configured.");
  }

  const token = await getIdToken(currentUser);

  // A thrown fetch (DNS, connection refused, CORS-network) means the endpoint is
  // unreachable — distinct from an authorization failure. Surface it as such so
  // login can keep the Firebase session and show an interstitial.
  let response: Response;
  try {
    response = await fetch(GRAPHQL_ENDPOINT, {
      method: "POST",
      signal,
      headers: {
        "content-type": "application/json",
        authorization: token,
        // Identifies the calling pop.in client app, matching the public GraphQL
        // contract the admin-gui uses.
        "X-App-Id": "mighty",
      },
      body: JSON.stringify({ query: SELF_QUERY }),
    });
  } catch (error) {
    throw new BackendUnavailableError("Could not reach the pop.in GraphQL endpoint.", { cause: error });
  }

  if (!response.ok) {
    throw new BackendUnavailableError(`Profile lookup failed (HTTP ${response.status}).`);
  }

  const body = (await response.json()) as SelfResponse;
  if (body.errors?.length) {
    throw new Error(body.errors.map((e) => e.message).join(", "));
  }

  const self = body.data?.self;
  if (!self) return null;

  const roles = (self.globalRoles?.nodes ?? [])
    .map((node) => node.permalink)
    .filter((permalink): permalink is string => Boolean(permalink));

  return {
    roles,
    displayName: self.public?.displayName ?? "",
    username: self.public?.username ?? "",
    photoURL: self.public?.photoURL ?? "",
  };
}

/** True when the given roles include one that may use the Partner Portal. */
export function hasPartnerAccess(roles: string[]): boolean {
  const allowed: readonly string[] = PARTNER_ROLES;
  return roles.some((role) => allowed.includes(role));
}

/** True when the given roles include the super-admin role (unlocks the admin
 *  "All brands" directory and cross-brand membership management). */
export function isSuperAdmin(roles: string[]): boolean {
  return roles.includes(SUPER_ADMIN_ROLE);
}
